import React, { useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import type { Moment } from 'moment';
import {
    RootState,
    useAppDispatch,
    useAppSelector,
    loadProjects,
    loadClients,
} from '../../../redux';
import {
    Ability,
    Button,
    ClientSelectField,
    Icon,
    Intervalpicker,
    LastActivity,
    Select,
} from '../../../components';
import type { Interval } from '../../../components';
import { Client, Project } from '../../../models';
import { clientPath } from '../../../utils/clientPath';

type Filters = {
    project?: Project;
    client?: Client;
    from: Moment;
    to: Moment;
};

function UserActivity() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state: RootState) => state.auth);
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const { clients } = useAppSelector((state: RootState) => state.clients);

    const [filters, setFilters] = useState<Filters>({
        from: moment().startOf('day'),
        to: moment().endOf('day'),
    });

    useEffect(() => {
        dispatch(loadProjects());
        dispatch(loadClients());
    }, [dispatch]);

    const projectOptions = useMemo(() => {
        return [...(projects || [])]
            .filter((item: Project) => item.active !== false)
            .filter((item: Project) => {
                if (!filters.client?._id)
                    return true;
                return (item.clients || []).some((client: Client | string) => (
                    (typeof client === 'string' ? client : client._id) === filters.client?._id
                ));
            })
            .sort((a: Project, b: Project) => a.name.localeCompare(b.name))
            .map((item: Project) => ({
                value: item._id,
                label: item.name,
            }));
    }, [projects, filters.client]);

    const applyInterval = (value: Interval) => {
        setFilters({
            ...filters,
            from: value?.startDate.startOf('day'),
            to: value?.endDate.endOf('day'),
        });
    };

    const onChangedProject = (projectId?: string | number) => {
        setFilters({
            ...filters,
            project: projects?.find((item: Project) => item._id === projectId),
        });
    };

    const onChangedClient = (clientId?: string | number) => {
        const client = clients?.find((item: Client) => item._id === clientId);
        const nextProject = filters.project && client
            ? ((filters.project.clients || []).some((c: Client | string) => (
                (typeof c === 'string' ? c : c._id) === client._id
            )) ? filters.project : undefined)
            : filters.project;

        setFilters({
            ...filters,
            client,
            project: clientId ? nextProject : filters.project,
        });
    };

    return (
        <Ability can="log:read" redirect="/">
            <div className="UserActivity">
                <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-0 sm:pl-6 lg:pl-8">
                    <div className="my-2 mx-auto lg:flex lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                <Icon type="activity" size={7} className="mr-1.5 inline" />
                                {t('app.user_activity')}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {t('app.user_activity_subtitle')}
                            </p>
                        </div>
                    </div>
                </header>
                <main className="m-5 space-y-4">
                    <div className="flex flex-col lg:flex-row justify-center items-center lg:items-start space-y-2 space-x-0 lg:space-x-2 lg:space-y-0">
                        <div className="flex-1 w-full">
                            <Intervalpicker
                                value={{ startDate: filters.from, endDate: filters.to }}
                                inputClassName="w-full rounded-md focus:ring-0 border-gray-900 font-bold bg-blue-100 dark:bg-blue-900/40 dark:bg-blue-900 dark:placeholder:text-blue-100 placeholder:text-sm"
                                toggleClassName="absolute bg-gray-900 border-1 border-gray-400 rounded-r-lg text-white right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                                separator={"->"}
                                onChange={applyInterval}
                                maxDate={moment()}
                                direction="down"
                            />
                        </div>
                        <div className="flex-1 flex w-full">
                            <Select
                                defaultValue={filters.project?._id}
                                options={projectOptions}
                                placeholder={t('tracks.select_project') || ''}
                                onChange={onChangedProject}
                                className="!mt-0 !w-full border border-gray-400 rounded-lg rounded-r-none"
                                containerClassName="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm shadow-md"
                                selectedClassName="h-[26px] font-bold flex items-center"
                                inline
                            />
                            <div className="flex flex-0 h-[44px] w-[45px] bg-gray-900 shadow-md border border-gray-400 border-l-0 rounded-lg rounded-l-none">
                                <Button
                                    title=""
                                    icon={filters.project ? 'close' : 'project'}
                                    color="none"
                                    iconColor="white"
                                    className="shadow-none"
                                    onClick={() => onChangedProject(undefined)}
                                />
                            </div>
                        </div>
                        <div className="relative z-20 flex flex-1 w-full">
                            <Formik
                                initialValues={filters}
                                enableReinitialize
                                onSubmit={() => {}}
                            >
                                <ClientSelectField
                                    name="client"
                                    onChange={(client?: Client) => { onChangedClient(client?._id); }}
                                    className="!mt-0 !w-full !p-0 border border-gray-400 rounded-lg rounded-r-none"
                                    selectedClassName="!text-gray-900 dark:text-gray-100 !bg-white dark:bg-gray-800 !text-sm font-bold"
                                    searchInputClassName="h-[42px] !ring-0"
                                    resultsClassName="!mt-0"
                                    value={filters.client}
                                />
                            </Formik>
                            {filters.client?._id && (
                                <Link
                                    to={clientPath(filters.client._id)}
                                    title={t('client_view.open_fiche') || ''}
                                    className="flex h-[44px] w-[45px] items-center justify-center border border-gray-400 border-l-0 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-md hover:bg-slate-100 dark:hover:bg-gray-700"
                                >
                                    <Icon type="eye" size={5} />
                                </Link>
                            )}
                            <div className="flex flex-0 h-[44px] w-[45px] bg-gray-900 shadow-md border border-gray-400 border-l-0 rounded-lg rounded-l-none">
                                <Button
                                    title=""
                                    icon={filters.client ? 'close' : 'client'}
                                    color="none"
                                    iconColor="white"
                                    className="shadow-none"
                                    onClick={() => onChangedClient(undefined)}
                                />
                            </div>
                        </div>
                    </div>
                    {user?._id && (
                        <LastActivity
                            author={user._id}
                            project={filters.project?._id}
                            client={filters.client?._id}
                            from={filters.from.toISOString()}
                            to={filters.to.toISOString()}
                            categoryMode="mine"
                            limit={100}
                            withHeader
                            title={t('app.user_activity')}
                        />
                    )}
                </main>
            </div>
        </Ability>
    );
}

export default UserActivity;
